"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  GraduationCap,
  Briefcase,
  Building2,
  Globe,
  Heart,
  Crown,
  Stethoscope,
  ArrowRight,
  Sparkles,
  Quote,
  Award,
} from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const BIO_CARDS = [
  {
    icon: GraduationCap,
    title: "Medical Foundation",
    body: "MBBS with a specialization in Gynaecology, Delhi. The discipline of medicine — diagnosis before treatment, protocol under pressure, evidence over assertion, accountability for outcomes — is the same discipline applied to sovereign-scale operations.",
    accent: "red",
    iconBg: "bg-gradient-sovereign",
    iconRing: "ring-red/30",
  },
  {
    icon: Briefcase,
    title: "Operational Leadership",
    body: "Oversees seamless execution of complex campaigns and governance programs across 18 countries. Ensures that sixty-plus integrated services operate in coordinated harmony — from real-time intelligence through secure communications.",
    accent: "purple",
    iconBg: "bg-gradient-royal",
    iconRing: "ring-purple/30",
  },
  {
    icon: Building2,
    title: "Co-Builder, Not Consultant",
    body: "CryptoMize was envisioned as the evolutionary merger of the pioneering work of its founders. Not a hired executive — a co-builder who has carried the operational mantle for a decade. Consultants advise. Builders deliver.",
    accent: "lime",
    iconBg: "bg-gradient-fresh",
    iconRing: "ring-lime/30",
  },
  {
    icon: Globe,
    title: "Global Stature",
    body: "Work has placed her alongside governments and global leaders in 30+ countries. Present at the Nobel Peace Prize and the World Economic Forum — stages reserved for those operating at the highest levels of international consequence.",
    accent: "cyan",
    iconBg: "bg-gradient-clarity",
    iconRing: "ring-cyan/30",
  },
  {
    icon: Heart,
    title: "The 70% Project",
    body: "Extremely vocal about women's empowerment, entrepreneurship, excellence, and equality. Teams assembled are over 70% women in most offices — a result that emerged from optimizing for people who do the work, communicate, stay, and make others better.",
    accent: "amber-gold",
    iconBg: "bg-gradient-premium",
    iconRing: "ring-amber-gold/30",
  },
  {
    icon: Crown,
    title: "Cross-Domain Mastery",
    body: "Expertise spans technology, politics, and medicine — an extremely rare blend that renders her invaluable in cross-domain strategic challenges. In a world of specialists, she is the integrator.",
    accent: "denim",
    iconBg: "bg-gradient-depth",
    iconRing: "ring-denim/30",
  },
];

const TEXT: Record<string, string> = {
  red: "text-red",
  purple: "text-purple",
  lime: "text-lime",
  cyan: "text-cyan",
  "amber-gold": "text-amber-gold",
  denim: "text-denim",
};

const STAT_COLORS = ["text-red", "text-purple", "text-cyan", "text-amber-gold", "text-emerald", "text-lime"];

export function AboutSection() {
  return (
    <Section id="about" ariaLabel="About Dr. Jyoti Kush" background="muted" pattern>
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
        {/* Left: Identity card — multi-color, NOT orange-dominated */}
        <div className="lg:sticky lg:top-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative glass-strong rounded-2xl gradient-border overflow-hidden"
          >
            {/* Multi-color corner glows — one per palette color */}
            <div aria-hidden className="absolute -top-20 -left-20 size-72 rounded-full bg-red/15 blur-3xl" />
            <div aria-hidden className="absolute -bottom-20 -right-20 size-72 rounded-full bg-purple/15 blur-3xl" />
            <div aria-hidden className="absolute top-1/2 -right-10 size-48 rounded-full bg-cyan/10 blur-2xl" />

            <div className="relative p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-red mb-6 border border-red/20">
                <Sparkles className="size-3.5 text-red" />
                <span className="text-overline text-red">About</span>
              </div>

              {/* Portrait — multi-color hero (royal/clarity), not orange */}
              <div className="relative mb-6 aspect-[3/4] rounded-xl overflow-hidden ring-1 ring-border/40">
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(135deg, oklch(0.55 0.20 295) 0%, oklch(0.50 0.18 265) 35%, oklch(0.70 0.14 200) 70%, oklch(0.66 0.17 155) 100%)",
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian-700/85 via-obsidian-700/30 to-transparent" />
                <Stethoscope className="size-32 text-white/35 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="text-display-sm leading-tight">Dr. Jyoti Kush</div>
                  <div className="text-body-sm opacity-85">MBBS, Gynaecology, Delhi</div>
                </div>
                <span
                  aria-hidden
                  className="absolute top-4 right-4 text-caption font-bold tracking-widest uppercase text-white/80 px-2.5 py-1 rounded-md backdrop-blur-md bg-white/10 border border-white/20"
                >
                  COO · CryptoMize
                </span>
              </div>

              <h3 className="text-heading-lg mb-2">Co-Architect of CryptoMize</h3>
              <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
                The Chief Operating Officer of CryptoMize and MaxiMize Infinium — the world&apos;s only
                full-spectrum Digital Conglomerate for Strategic Sovereignty.
              </p>

              {/* Stats — 6 distinct non-pink colors */}
              <div className="grid grid-cols-3 gap-2.5">
                {[
                  { v: "10+", l: "Years", c: "text-red" },
                  { v: "60+", l: "Services", c: "text-purple" },
                  { v: "18", l: "Countries", c: "text-cyan" },
                  { v: "70%", l: "Women", c: "text-amber-gold" },
                  { v: "0", l: "Breaches", c: "text-emerald" },
                  { v: "15+", l: "Languages", c: "text-lime" },
                ].map((s) => (
                  <div key={s.l} className="rounded-lg bg-muted/40 p-3 border border-border/30">
                    <div className={`text-heading-md ${s.c} font-bold`}>{s.v}</div>
                    <div className="text-caption text-muted-foreground">{s.l}</div>
                  </div>
                ))}
              </div>

              <Link
                href="/about/"
                className="mt-6 inline-flex items-center gap-2 text-body-sm font-semibold text-red hover:gap-3 transition-all group"
              >
                Read full biography
                <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Right: BIO_CARDS — each with its OWN gradient icon (no orange) */}
        <div>
          <SectionHeader
            icon={Quote}
            overline="Biography, Credentials, Background"
            overlineClassName="text-red"
            pillClassName="glass-tinted-red border-red/20"
            align="left"
            title={
              <>
                A physician who commands <br />
                <span className="text-gradient-royal">sovereign-scale operations.</span>
              </>
            }
          />
          <div className="space-y-3">
            {BIO_CARDS.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`flex items-start gap-4 p-5 rounded-xl glass-card border border-current/15 group hover:scale-[1.005] transition-all`}
              >
                <div
                  className={`shrink-0 size-11 rounded-lg ${c.iconBg} flex items-center justify-center shadow-md ring-2 ${c.iconRing} group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}
                >
                  <c.icon className="size-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-caption font-bold ${TEXT[c.accent]} uppercase tracking-wider`}
                  >
                    {c.title}
                  </div>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mt-1.5">
                    {c.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}
